/**
 * RecordAndCompare — micro mini-studio.
 *
 * Affiche, après réponse à un exercice audio, deux mini-lecteurs côte à côte :
 *   1. l'audio TTS de référence
 *   2. l'enregistrement de la voix de l'utilisateur
 *
 * Aucun envoi serveur (V1) — tout reste en mémoire / Blob URL.
 * Conçu pour être imbriqué dans `ExerciseSeriesPlayer` après réponse.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Play, Pause, RefreshCcw, Volume2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('http') ? u : `${API_BASE}${u || ''}`);

const MAX_RECORD_MS = 8000;

const Player = ({ src, label, accent = 'primary', testid }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = new Audio(src);
    a.onended = () => setPlaying(false);
    a.onerror = () => setPlaying(false);
    ref.current = a;
    return () => { a.pause(); ref.current = null; };
  }, [src]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.currentTime = 0; a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const dotCls = accent === 'primary'
    ? 'bg-primary/15 text-primary border-primary/40 hover:bg-primary/25'
    : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25';

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <button
        type="button"
        onClick={toggle}
        className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${dotCls}`}
        aria-label={playing ? 'Pause' : 'Lecture'}
        data-testid={`${testid}-toggle`}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <span className="text-xs text-text-secondary">{label}</span>
    </div>
  );
};

export const RecordAndCompare = ({ ttsUrl, testidPrefix = 'rec' }) => {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [userBlob, setUserBlob] = useState(null);
  const [userUrl, setUserUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setSupported(false);
    }
    return () => {
      if (recRef.current && recRef.current.state !== 'inactive') {
        try { recRef.current.stop(); } catch { /* ignore */ }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (userUrl) URL.revokeObjectURL(userUrl);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []); // eslint-disable-line

  const start = async () => {
    if (recording || !supported) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (userUrl) URL.revokeObjectURL(userUrl);
        const url = URL.createObjectURL(blob);
        setUserBlob(blob);
        setUserUrl(url);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mr.start();
      recRef.current = mr;
      startedAtRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      tickRef.current = setInterval(() => {
        const ms = Date.now() - startedAtRef.current;
        setElapsed(ms);
        if (ms >= MAX_RECORD_MS) stop();
      }, 100);
    } catch (e) {
      setSupported(false);
    }
  };

  const stop = () => {
    if (!recording) return;
    setRecording(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { recRef.current?.stop(); } catch { /* ignore */ }
  };

  const reset = () => {
    if (userUrl) URL.revokeObjectURL(userUrl);
    setUserBlob(null);
    setUserUrl(null);
    setElapsed(0);
  };

  if (!supported) {
    return (
      <div
        className="rounded-md border border-border bg-surface-elevated/30 p-3 text-xs text-text-muted"
        data-testid={`${testidPrefix}-unsupported`}
      >
        Le micro n'est pas accessible sur ce navigateur.
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-3"
      data-testid={`${testidPrefix}-block`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs text-primary">
          <Mic className="w-3.5 h-3.5" />
          <span className="uppercase tracking-wider">Compare ta voix</span>
        </div>
        {recording && (
          <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40 text-[10px]">
            {(elapsed / 1000).toFixed(1)}s / {(MAX_RECORD_MS / 1000).toFixed(0)}s
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {ttsUrl && (
          <Player
            src={absUrl(ttsUrl)}
            label="Référence"
            accent="primary"
            testid={`${testidPrefix}-tts`}
          />
        )}

        {!recording && !userUrl && (
          <Button
            size="sm"
            variant="outline"
            onClick={start}
            data-testid={`${testidPrefix}-start`}
          >
            <Mic className="w-4 h-4 mr-1" /> Enregistrer
          </Button>
        )}

        {recording && (
          <Button
            size="sm"
            variant="destructive"
            onClick={stop}
            data-testid={`${testidPrefix}-stop`}
          >
            <Square className="w-4 h-4 mr-1" /> Stop
          </Button>
        )}

        {userUrl && (
          <>
            <Player
              src={userUrl}
              label="Ma voix"
              accent="emerald"
              testid={`${testidPrefix}-user`}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              data-testid={`${testidPrefix}-reset`}
              className="text-xs text-text-muted"
            >
              <RefreshCcw className="w-3 h-3 mr-1" /> Refaire
            </Button>
          </>
        )}
      </div>

      <p className="text-[11px] text-text-muted">
        Écoute la référence puis enregistre-toi (8 s max). Les deux extraits restent
        en local — rien n'est envoyé sur le serveur.
      </p>
    </div>
  );
};

export default RecordAndCompare;
